import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlask, faFileText } from "@fortawesome/free-solid-svg-icons";
import Menu from "../../components/Menu";
import moment from "moment";
import api from "../../services/api";
import PatientCard from "../../components/PatientCard";
import Swal from "sweetalert2";
import PatientPartialCard from "../../components/PatientPartialCard";
import '../../components/componentsCss/PosConsultPageHome.css';
import LoadingBlock from "../../components/LoadingBlock";
import BudgetProceduresModal from "../../components/BudgetProceduresModal";
import { usePusherChannel } from '../../hooks/usePusherChannel';
import ProceduresModal from "../../components/ProceduresModal";
import DoctorSelectionPage from "../../components/DoctorSelectionPage.js";
import SchedulingPage from "../../components/SchedulingPage.js";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "../../contexts/ContextProvider.js";
import ConfirmationPage from "../../components/ConfirmationPage.js";
import ScheduledProceduresModal from "../../components/ScheduledProceduresModal.js";
import PaymentModal from "../../components/PaymentModal.js";

export default function SalesExams() {
  const navigate = useNavigate();
  const { user } = useStateContext();

  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [search, setSearch] = useState("");

  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showScheduledModal, setShowScheduledModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [currentPage, setCurrentPage] = useState("main");

  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedProcedures, setSelectedProcedures] = useState([]);

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [schedulingDetails, setSchedulingDetails] = useState({
    date: "",
    time: "",
    comments: "",
  });

  const normalizedPatients = patients.map(normalizePatientData);

  const fetchData = async (loading = true) => {
    setLoading(loading);

    try {
      const response = await api.post('/api/clinic-procedures/patient-summary-all-day-new', {
        date
      });

      setPatients(response.data.data);
    } catch (error) {
      console.error("Erro ao buscar atendimentos:", error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData()
  }, [date])

  usePusherChannel(
    'update-pos-doctor-attendance-sale-channel',
    'update-pos-doctor-attendance-sale-event',
    async () => {
      await fetchData(false);
    }
  )

  usePusherChannel(
    "update-pos-pharmacy-sale-channel",
    "update-pos-pharmacy-sale-event",
    async () => {
      await fetchData(false);
    }
  );

  usePusherChannel(
    "update-pharmacy-sale-channel",
    "update-pharmacy-sale-event",
    async () => {
      await fetchData(false);
    }
  );

  usePusherChannel(
    "update-pos-doctor-pharmacy-sale-channel",
    "update-pos-doctor-pharmacy-sale-event",
    async () => {
      await fetchData(false);
    }
  );

  const filteredPatients = useMemo(() => {
    if (!Array.isArray(normalizedPatients)) return [];
    return normalizedPatients.filter((entry) => {
      const name = entry?.name?.toLowerCase() || "";
      const cpf = entry?.cpf || "";
      return (
        name.includes(search.toLowerCase()) || cpf.includes(search)
      );
    });
  }, [normalizedPatients, search]);

  function normalizePatientData(raw) {
    if (raw.patient) {
      return {
        id: raw.patient.id,
        name: raw.patient.name,
        cpf: raw.patient.cpf,
        procedures: raw.pending || [],
        ...raw,
      };
    } else {
      return {
        id: raw.id,
        name: raw.name,
        cpf: raw.cpf,
        procedures: [], // ou algo similar se você quiser simular
        ...raw,
      };
    }
  }

  const getSellingPrice = (proc, doctor) => {
    const byId =
      doctor?.procedures?.find((p) => p.id === proc.procedure_id)
        ?.selling_price ??
      doctor?.procedures?.find((p) => p.id === proc.id)?.selling_price;

    if (typeof byId === "number" && !isNaN(byId)) return byId;

    return Number(proc.value ?? proc.cost_price ?? proc.price ?? 0);
  };

  const getProcCostPrice = (proc, doctor) => {
    const byId =
      doctor?.procedures?.find((p) => p.id === proc.procedure_id)?.cost_price ??
      doctor?.procedures?.find((p) => p.id === proc.id)?.cost_price;

    if (typeof byId === "number" && !isNaN(byId)) return byId;

    return Number(proc.cost_price ?? 0);
  };

  const handlePatientDetails = (patient) => {
    setSelectedPatient(patient);
  };

  const handleOpenModal = (patient) => {
    setSelectedPatient(patient);
    setShowModal(true);
    setCurrentPage("main");
  };

  const handleProceedToSelectDoctor = (procedures) => {
    setSelectedProcedures(procedures);
    setCurrentPage("doctorSelection");
    setShowModal(false); // Fechamos o modal porque agora vamos para uma página completa
  };

  const handleSelectDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setCurrentPage("scheduling");
  };

  const handleSchedulingSubmit = (details) => {
    setSchedulingDetails(details);
    setCurrentPage("confirmation");
  };

  const handleConfirmScheduling = () => {
    // Aqui você implementaria a lógica para salvar o agendamento no backend
    // console.log("Agendamento confirmado:", {
    //   patient: selectedPatient,
    //   procedures: selectedProcedures,
    //   doctor: selectedDoctor,
    //   details: schedulingDetails,
    // });

    const handleConfirm = async () => {
      if (
        !schedulingDetails.date ||
        !schedulingDetails.time ||
        !schedulingDetails.comments
      ) {
        Swal.fire({
          title: "Erro",
          text: "Por favor, preencha todos os campos antes de confirmar.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      // const formattedDate = moment(schedulingDetails.date).format(
      //   "YYYY-MM-DD HH:mm:ss"
      // );
      // const timeBooked = moment(schedulingDetails.time).format("HH:mm");

      const payload = {
        affiliate_company_id: selectedDoctor?.id || 0, // fallback padrão
        clinic_id:
          selectedDoctor?.clinic_id || selectedProcedures?.[0]?.clinic_id || 0,
        status: "0", // status inicial (ex: pendente)
        customer_cpf: selectedPatient?.cpf,
        customer_birth: selectedPatient?.patient?.birth || "",
        professional_name: selectedDoctor?.name,
        comment: schedulingDetails.comments,
        scheduling_date: `${schedulingDetails.date} ${schedulingDetails.time}:00`,
        executing_company_id: selectedDoctor.id || 0,
        procedures: selectedProcedures.map((procedure) => ({
          user_id: user.user.id,
          procedure_id: procedure.procedure_id || procedure.id,
          cbhpm_code: procedure.cbhpm_code || null,
          clinic_procedure_id: procedure.id,
          clinic_id: procedure.clinic_id,
          scheduling_id_secondary: procedure.scheduling_id_secondary,
          quantity: 1,
          agreement:
            procedure.payment?.type === "insurance"
              ? procedure.payment?.insurance
              : null,
          cost_price: getProcCostPrice(procedure, selectedDoctor),
          selling_price: getSellingPrice(procedure, selectedDoctor),
          requesting_company: procedure.professional_name || "",
          executing_company: selectedDoctor.name || "",
          insurance: procedure.payment?.insurance || null,
          date_scheduling: schedulingDetails.date,
          time_booked: `${schedulingDetails.time}:00`,
          price: getSellingPrice(procedure, selectedDoctor),
        })),
      };

      Swal.fire({
        title: "Aguarde",
        text: "Confirmando agendamento...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      })

      try {
        const response = await api.post("/api/schedulings", payload);
        console.log("🚀 ~ handleConfirmScheduling ~ payload:", payload);

        Swal.close();

        Swal.fire({
          title: "Sucesso",
          text: "Agendamento confirmado com sucesso!",
          icon: "success",
          confirmButtonText: "OK",
        });
        console.log(response.data);
      } catch (error) {
        console.error(
          "Erro ao confirmar agendamento: aqui o que o sistem cuspiu:",
          error
        );
        Swal.fire({
          title: "Erro",
          text: "Ocorreu um erro ao confirmar o agendamento. Tente novamente.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
      await fetchData();
    };

    // Voltar para a tela principal
    setCurrentPage("main");
    setSelectedProcedures([]);
    setSelectedDoctor(null);
    setSchedulingDetails({
      date: "",
      time: "",
      comments: "",
    });
    handleConfirm();
  };

  const handleOpenScheduledModal = async (patient) => {
    try {
      const response = await api.post("/api/schedulings/getByCpf", {
        cpf: patient.cpf,
        include_trashed: true,
      });

      const schedules = response.data.data || [];
      console.log("Agendamentos: ", schedules);

      // Atualiza o paciente com os agendamentos recebidos
      setSelectedPatient({
        ...patient,
        schedules: schedules, // <-- ESSENCIAL para o ScheduledProceduresModal
        procedures_by_date: groupProceduresByDate(schedules), // ← aqui
      });

      setShowScheduledModal(true);
    } catch (error) {
      console.error("Erro ao buscar agendamentos por CPF:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível buscar os agendamentos.",
      });
    }
  };

  function groupProceduresByDate(procedures) {
    const grouped = {};

    procedures.forEach((proc) => {
      const date = proc.created_at?.split("T")[0] ?? "sem-data";

      if (!grouped[date]) {
        grouped[date] = {
          cancelled: [],
          confirmed: [],
          scheduled: [],
        };
      }

      if (proc.scheduled) {
        grouped[date].scheduled.push(proc);
      } else if (proc.confirmed) {
        grouped[date].confirmed.push(proc);
      } else {
        grouped[date].cancelled.push(proc);
      }
    });

    return grouped;
  }

  const handleOpenBudgetModal = (patient) => {
    setSelectedPatient(patient);
    setShowBudgetModal(true);
  }

  const handleCloseModal = () => {
    setShowModal(false);
    setShowScheduledModal(false);
    setShowBudgetModal(false);
    setCurrentPage("main");
    setSelectedProcedures([]);
    setSelectedDoctor(null);
    setSchedulingDetails({
      date: "",
      time: "",
      comments: "",
    });
    setSelectedPatient(null);
  };

  const handleBackToMain = () => {
    setCurrentPage("main");
  };

  const handleBackToDoctorSelection = () => {
    setCurrentPage("doctorSelection");
  };

  const handleBackToScheduling = () => {
    setCurrentPage("scheduling");
  };

  const handleReschedule = async (payload) => {
    try {
      const response = await api.post(
        "/api/schedulings/reschedule-items",
        payload
      );

      Swal.fire({
        title: "Sucesso",
        text: "Procedimentos reagendados com sucesso!",
        icon: "success",
      });

      await fetchData();
      handleCloseModal(); // fecha modais e reseta estado

      console.log("✅ Reagendamento realizado:", response.data);
    } catch (error) {
      console.error("❌ Erro ao reagendar procedimentos:", error);
      Swal.fire({
        title: "Erro",
        text: "Não foi possível reagendar os procedimentos.",
        icon: "error",
      });
    }
  };
  // ScheduledProceduresModal.js
  const handleSavePayment = async (formData) => {
    try {
      console.log("Arquivo PDF:", formData.get("merged_receipt"));
      console.log("JSON data:", formData.get("data"));

      Swal.fire({
        title: "Aguarde",
        text: "Confirmando pagamento...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      })

      // não precisa setar Content-Type; o browser/Axios cuida do boundary
      await api.post("/api/cashiers", formData, {
        headers: {
          // não ponha boundary—o Axios cuida dele—só garanta que é multipart
          "Content-Type": "multipart/form-data",
          // opcionalmente:
          Accept: "application/json",
        },
      });
      await fetchData();
      Swal.close();
      Swal.fire("Sucesso", "Pagamento registrado com sucesso.", "success");
    } catch (err) {
      console.error(err);
      Swal.close();
      Swal.fire("Erro", "Falha ao registrar pagamento.", "error");
    }
  };

  const removeSchedule = async (
    schedules,
    schedulesIds,
    requisition_id_foreign
  ) => {
    if (!Array.isArray(schedules) || schedules.length === 0) {
      console.warn("Nenhum procedimento selecionado para cancelar.");
      return;
    }

    try {
      // const response = await api.post(`/api/clinic-procedures/cancel`, {
      //  ids: schedules,
      //});

      //console.log("✅ Cancelamento realizado:", response.data);

      // Se estiver usando SweetAlert2
      // Swal.fire("Sucesso", response.data.message, "success");

      //Swal.fire({
      //  title: "Sucesso",
      //  text: "Procedimento cancelado com sucesso!",
      //  icon: "success",
      //  confirmButtonText: "OK",
      //});
      // 2. Remove os itens de agendamento do scheduling
      const response2 = await api.post(`/api/schedulings/remove-items`, {
        items: schedules.map((s) => ({
          scheduling_id: schedulesIds[0],
          procedure_id: s.procedure_id,
          requisition_id_foreign: s.requisition_id_foreign,
        })),
      });
      console.log("IDs Scheduling para remover: ", schedulesIds);
      console.log("IDs Procedures para remover: ", schedules);
      console.log("IDs Requisições para remover: ", requisition_id_foreign);
      console.log("✅ Itens removidos:", response2.data);
      await fetchData();
      Swal.fire({
        title: "Sucesso",
        text: "Procedimento cancelado com sucesso!",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("❌ Erro ao cancelar procedimentos:", error);

      //Swal.fire({
      //  title: "Erro",
      //  text: error.response?.data?.message || "Erro inesperado.",
      //  icon: "error",
      //  confirmButtonText: "OK",
      //});
      Swal.fire({
        title: "Erro",
        text: error.response?.data?.message || "Erro inesperado.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleRemoveProcedures = (
    schedules,
    schedulesIds,
    requisition_id_foreign
  ) => {
    removeSchedule(schedules, schedulesIds, requisition_id_foreign);
    console.log("IDs Scheduling para remover: ", schedulesIds);
    console.log("IDs Procedures para remover: ", schedules);
    console.log("IDs Requisições para remover: ", requisition_id_foreign);
    handleCloseModal();
  };

  // Adicionar a função para abrir o modal de pagamento diretamente
  const handleMakePayment = async (patient) => {
    try {
      const response = await api.post("/api/schedulings/getByCpf", {
        cpf: patient.cpf,
        include_trashed: true,
      });

      const schedules = response.data.data || [];

      if (!schedules.length) {
        Swal.fire({
          title: "Erro",
          text: "Não há agendamentos encontrados para este paciente.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      // Atualiza o estado com o paciente e seus agendamentos
      setSelectedPatient({
        ...patient,
        schedules,
      });

      setSelectedSchedule({ schedules });
      setShowPaymentModal(true);
    } catch (error) {
      console.error("❌ Erro ao buscar agendamentos para pagamento:", error);
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar agendamentos para pagamento.",
        icon: "error",
      });
    }
  };

  // Adicionar a função para fechar o modal de pagamento
  const handleClosePayment = () => {
    setShowPaymentModal(false);
  };

  useEffect(() => {
    const handleUpdateDate = (event) => {
      const today = new Date().toISOString().split("T")[0];
      if (date !== today) {
        setDate(today);
      }
    };

    window.addEventListener('updateDateAndRefetch', handleUpdateDate);

    return () => {
      window.removeEventListener('updateDateAndRefetch', handleUpdateDate);
    };
  }, [date]);

  const screenTela =
    <div className="ms-4 mt-3">
      <div className="row row-container">
        {currentPage === "main" && (
          <>
            <div className="d-flex align-items-center justify-content-evenly align-items-center">
              <FontAwesomeIcon icon={faFlask} size="2xl" className="me-4" style={{ color: '#00468B' }} />

              <h1 className="col-6 col-sm-6 col-lg-6 name-screen my-4"> <span className="name-screen-title" >Central de Exames</span> </h1>

              <input style={{ maxWidth: 165 }} value={date} onChange={(value) => {
                if (value.target.value === '') return
                setDate(value.target.value)
              }} type="date" placeholder="Pesquisar por Nome ou CPF" className="form-control input-pesquisa-non-focus me-3" />

              <input value={search} onChange={(value) => {
                setSearch(value.target.value.toUpperCase())
              }} type="text" placeholder="Pesquisar por Nome ou CPF" className="form-control input-pesquisa-vendas" />
            </div>

            <div className="patients-list">

              {loading ? (
                <LoadingBlock />
              ) : (
                Array.isArray(filteredPatients) &&
                filteredPatients.map((patient) => (
                  <>
                    {
                      (patient.patient.birth && patient.patient.cpf)
                        ?
                        <PatientCard
                          key={patient.id}
                          patient={patient}
                          onViewDetails={() => handlePatientDetails(patient)}
                          onViewProcedures={() => handleOpenModal(patient)}
                          onViewScheduledProcedures={() =>
                            handleOpenScheduledModal(patient)
                          }
                          onMakePayment={() => handleMakePayment(patient)}
                          onViewBudget={() => handleOpenBudgetModal(patient)}
                        //onWhatsappOpen={() => openWhatsApp(patient)}
                        />
                        :
                        <PatientPartialCard
                          key={`impressora-${patient.id}}`}
                          patient={patient}
                        />
                    }

                  </>
                ))
              )}

              {Array.isArray(filteredPatients) &&
                !loading &&
                filteredPatients.length === 0 && (
                  <div className="no-results">
                    <FontAwesomeIcon icon={faFileText} size="3x" />
                    <p>Nenhum paciente encontrado</p>
                  </div>
                )}
            </div>

            {showBudgetModal && selectedPatient && (
              <BudgetProceduresModal
                open={showBudgetModal}
                patient={selectedPatient}
                onClose={handleCloseModal}
                onRefresh={fetchData}
              // onRemove={handleRemoveProcedures}
              // onReschedule={handleReschedule}
              />
            )}

            {showModal && selectedPatient && (
              <ProceduresModal
                patient={selectedPatient}
                onClose={handleCloseModal}
                onProceed={handleProceedToSelectDoctor}
              />
            )}

            {showScheduledModal && selectedPatient && (
              <ScheduledProceduresModal
                patient={selectedPatient}
                onClose={handleCloseModal}
                onRemove={handleRemoveProcedures}
                onReschedule={handleReschedule}
              />
            )}
          </>
        )}

        {currentPage === "doctorSelection" && selectedPatient && (
          <DoctorSelectionPage
            patient={selectedPatient}
            procedures={selectedProcedures}
            onSelectDoctor={handleSelectDoctor}
            onBack={handleBackToMain}
          />
        )}

        {currentPage === "scheduling" && selectedPatient && selectedDoctor && (
          <SchedulingPage
            patient={selectedPatient}
            procedures={selectedProcedures}
            doctor={selectedDoctor}
            onSubmit={handleSchedulingSubmit}
            onBack={handleBackToDoctorSelection}
          />
        )}

        {currentPage === "confirmation" &&
          selectedPatient &&
          selectedDoctor && (
            <ConfirmationPage
              patient={selectedPatient}
              procedures={selectedProcedures}
              doctor={selectedDoctor}
              details={schedulingDetails}
              onConfirm={handleConfirmScheduling}
              onBack={handleBackToScheduling}
            />
          )}

        {showPaymentModal && selectedPatient && selectedSchedule && (
          <PaymentModal
            schedule={selectedSchedule}
            patient={selectedPatient}
            onSavePayment={handleSavePayment}
            onClose={handleClosePayment}
          />
        )}
      </div>
    </div>

  return (
    <Menu screen={screenTela} />
  )
}